/**
 * Shared searchable character-node catalog picker.
 * Used by Studio (and available to ProductionWorkflowPage).
 */
import React, { useEffect, useId, useMemo, useRef } from 'react';
import {
  NODE_CATEGORIES,
  PICKER_NODE_TYPES,
  defaultPortsForType,
  getNodeDef,
} from './productionWorkflow';
import { portsCompatible } from './characterNodeCatalog';

export function filterPickerDefs({
  query = '',
  includeAdmin = false,
  sourcePortType = null,
} = {}) {
  const q = query.trim().toLowerCase();
  return PICKER_NODE_TYPES.filter((def) => {
    if (def.adminOnly && !includeAdmin) return false;
    if (sourcePortType) {
      const inputs = defaultPortsForType(def.type).inputs || [];
      if (!inputs.some((p) => portsCompatible(sourcePortType, p.type))) return false;
    }
    if (!q) return true;
    return (
      def.label.toLowerCase().includes(q) ||
      (def.activity || '').toLowerCase().includes(q) ||
      (def.description || '').toLowerCase().includes(q) ||
      def.type.toLowerCase().includes(q)
    );
  });
}

/** First compatible input port id on the target type for an output port type. */
export function firstCompatibleInputPort(nodeType, sourcePortType) {
  const inputs = defaultPortsForType(nodeType).inputs || [];
  const hit = inputs.find((p) => portsCompatible(sourcePortType, p.type));
  return hit?.id || inputs[0]?.id || 'in';
}

export function sourceOutputPortType(graph, nodeId, portId) {
  const node = (graph?.nodes || []).find((n) => n.id === nodeId);
  if (!node) return null;
  const outputs = defaultPortsForType(node.type).outputs || [];
  const port = outputs.find((p) => p.id === (portId || 'out')) || outputs[0];
  return port?.type || null;
}

/**
 * @param {{
 *   open: boolean,
 *   clientX?: number,
 *   clientY?: number,
 *   query?: string,
 *   onQueryChange?: (q: string) => void,
 *   onPick: (type: string) => void,
 *   onClose: () => void,
 *   includeAdmin?: boolean,
 *   sourcePortType?: string | null,
 *   title?: string,
 *   className?: string,
 * }} props
 */
export default function CatalogNodePicker({
  open,
  clientX = 24,
  clientY = 24,
  query = '',
  onQueryChange,
  onPick,
  onClose,
  includeAdmin = false,
  sourcePortType = null,
  title = 'Add node',
  className = '',
}) {
  const searchId = useId();
  const inputRef = useRef(null);
  const defs = useMemo(
    () => filterPickerDefs({ query, includeAdmin, sourcePortType }),
    [query, includeAdmin, sourcePortType]
  );

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const byCategory = NODE_CATEGORIES.map((cat) => ({
    ...cat,
    items: defs.filter((d) => d.category === cat.id),
  })).filter((c) => c.items.length);

  return (
    <div
      className={`catalog-node-picker ${className}`}
      role="dialog"
      aria-label={title}
      style={{ left: clientX, top: clientY }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <header>
        <strong>{title}</strong>
        {sourcePortType ? (
          <span className="catalog-node-picker-hint">
            Compatible with {sourcePortType} output
          </span>
        ) : null}
      </header>
      <label className="visually-hidden" htmlFor={searchId}>
        Search nodes
      </label>
      <input
        id={searchId}
        ref={inputRef}
        className="catalog-node-picker-search"
        placeholder="Search nodes…"
        value={query}
        onChange={(e) => onQueryChange?.(e.target.value)}
      />
      <div className="catalog-node-picker-list">
        {byCategory.length === 0 ? (
          <p className="catalog-node-picker-empty" role="status">
            No matching nodes.
          </p>
        ) : (
          byCategory.map((cat) => (
            <div key={cat.id} className="catalog-node-picker-group">
              <span>{cat.label}</span>
              <ul>
                {cat.items.map((def) => (
                  <li key={def.type}>
                    <button type="button" onClick={() => onPick(def.type)}>
                      <strong>{def.label}</strong>
                      <span>
                        {def.activity || def.category}
                        {def.availability === 'unavailable' ? ' · unavailable' : ''}
                        {def.availability === 'worker_mock' ? ' · mock worker' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
      <button type="button" className="catalog-node-picker-close" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

export { getNodeDef, PICKER_NODE_TYPES, NODE_CATEGORIES };
